/**
 * SlidePanel Component for Poker Reborn
 *
 * A panel that slides in from the left or right edge of the screen.
 * Click the backdrop or close button to dismiss.
 * Add any content as children to the content container.
 *
 * Uses Ticker.shared + easing for smooth animation.
 */

import { Container, Graphics, Text, Ticker } from 'pixi.js';
import { easeOutCubic, easeInCubic } from '@/effects/utils/easing';
import { audioService } from '@/services/AudioService';

// =============================================================================
// TYPES
// =============================================================================

export interface SlidePanelOptions {
  /** Screen width */
  screenWidth: number;

  /** Screen height */
  screenHeight: number;

  /** Panel width as fraction of screen width (0-1). Default: 0.6 */
  widthFraction?: number;

  /** Which side the panel slides in from. Default: 'left' */
  side?: 'left' | 'right';

  /** Panel title */
  title?: string;

  /** Animation duration in ms. Default: 300 */
  animationDuration?: number;

  /** Backdrop alpha when fully open. Default: 0.5 */
  backdropAlpha?: number;

  /** Panel background color. Default: 0x1a1a2e */
  backgroundColor?: number;

  /** Panel border color. Default: 0x3a3a5e */
  borderColor?: number;

  /** Show close button (×). Default: true */
  showCloseButton?: boolean;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const PANEL_DEFAULTS = {
  widthFraction: 0.6,
  animationDuration: 300,
  backdropAlpha: 0.5,
  backgroundColor: 0x1a1a2e,
  borderColor: 0x3a3a5e,
  titleFontSize: 20,
  titleColor: 0xffffff,
  padding: 20,
  closeButtonSize: 28,
  closeButtonColor: 0x888888,
  closeButtonHoverColor: 0xffffff,
};

// =============================================================================
// SLIDE PANEL
// =============================================================================

export class SlidePanel extends Container {
  /** Fires when the panel finishes closing and destroys itself */
  public onDismiss?: () => void;

  private backdrop: Graphics;
  private panelBody: Container;
  private panelBackground: Graphics;
  private titleText: Text | null = null;
  private closeButton: Container | null = null;
  private contentContainer: Container;

  private screenWidth: number;
  private screenHeight: number;
  private panelWidth: number;
  private widthFraction: number;
  private animationDuration: number;
  private backdropAlpha: number;
  private backgroundColor: number;
  private borderColor: number;
  private side: 'left' | 'right';

  // Animation state
  private animating = false;
  private animationProgress = 0; // 0 = closed, 1 = open
  private animationDirection: 'open' | 'close' = 'open';
  private animationElapsed = 0;

  constructor(options: SlidePanelOptions) {
    super();

    this.screenWidth = options.screenWidth;
    this.screenHeight = options.screenHeight;
    this.widthFraction = options.widthFraction ?? PANEL_DEFAULTS.widthFraction;
    this.animationDuration = options.animationDuration ?? PANEL_DEFAULTS.animationDuration;
    this.backdropAlpha = options.backdropAlpha ?? PANEL_DEFAULTS.backdropAlpha;
    this.backgroundColor = options.backgroundColor ?? PANEL_DEFAULTS.backgroundColor;
    this.borderColor = options.borderColor ?? PANEL_DEFAULTS.borderColor;
    this.side = options.side ?? 'left';
    this.panelWidth = Math.round(this.screenWidth * this.widthFraction);

    // Backdrop (full-screen, semi-transparent)
    this.backdrop = new Graphics();
    this.backdrop.eventMode = 'static';
    this.backdrop.cursor = 'pointer';
    this.backdrop.on('pointerdown', () => { this.close(); });
    this.addChild(this.backdrop);

    // Panel body (slides in from the configured side)
    this.panelBody = new Container();
    this.panelBody.eventMode = 'static'; // Block events from passing through
    this.addChild(this.panelBody);

    // Panel background
    this.panelBackground = new Graphics();
    this.panelBody.addChild(this.panelBackground);

    // Title
    if (options.title) {
      this.titleText = new Text({
        text: options.title,
        style: {
          fill: PANEL_DEFAULTS.titleColor,
          fontSize: PANEL_DEFAULTS.titleFontSize,
          fontFamily: 'Roboto, Arial, sans-serif',
          fontWeight: 'bold',
        },
      });
      this.titleText.anchor.set(0, 0);
      this.titleText.x = PANEL_DEFAULTS.padding;
      this.titleText.y = PANEL_DEFAULTS.padding;
      this.panelBody.addChild(this.titleText);
    }

    // Close button
    if (options.showCloseButton !== false) {
      this.createCloseButton();
    }

    // Content container (where consumers add children)
    this.contentContainer = new Container();
    const contentY = PANEL_DEFAULTS.padding
      + (this.titleText ? PANEL_DEFAULTS.titleFontSize + PANEL_DEFAULTS.padding : 0);
    this.contentContainer.x = PANEL_DEFAULTS.padding;
    this.contentContainer.y = contentY;
    this.panelBody.addChild(this.contentContainer);

    // Block all events on this container
    this.eventMode = 'static';

    // Draw initial state
    this.drawBackdrop(0);
    this.drawPanel();

    // Position panel off-screen (closed)
    this.panelBody.x = this.side === 'left' ? -this.panelWidth : this.screenWidth;

    // Start open animation
    this.startAnimation('open');
  }

  // ===========================================================================
  // PUBLIC API
  // ===========================================================================

  /** Get the content container to add children */
  getContentContainer(): Container {
    return this.contentContainer;
  }

  /** Get the usable content area dimensions */
  getContentArea(): { width: number; height: number } {
    const titleHeight = this.titleText
      ? PANEL_DEFAULTS.titleFontSize + PANEL_DEFAULTS.padding
      : 0;
    return {
      width: this.panelWidth - PANEL_DEFAULTS.padding * 2,
      height: this.screenHeight - PANEL_DEFAULTS.padding * 2 - titleHeight,
    };
  }

  /** Close the panel with animation */
  close(): void {
    if (this.animating && this.animationDirection === 'close') return;
    this.startAnimation('close');
  }

  /** Resize the panel */
  resize(screenWidth: number, screenHeight: number): void {
    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;
    this.panelWidth = Math.round(screenWidth * this.widthFraction);

    this.drawBackdrop(this.animationProgress);
    this.drawPanel();
    this.updatePositions();
  }

  // ===========================================================================
  // ANIMATION
  // ===========================================================================

  private startAnimation(direction: 'open' | 'close'): void {
    this.animationDirection = direction;
    this.animationElapsed = 0;

    if (!this.animating) {
      this.animating = true;
      Ticker.shared.add(this.onAnimationTick);
    }

    if (direction === 'open') {
      audioService.play('popup_open');
    }
  }

  private onAnimationTick = (): void => {
    this.animationElapsed += Ticker.shared.deltaMS;
    const rawProgress = Math.min(this.animationElapsed / this.animationDuration, 1);

    if (this.animationDirection === 'open') {
      this.animationProgress = easeOutCubic(rawProgress);
    } else {
      this.animationProgress = 1 - easeInCubic(rawProgress);
    }

    this.updatePositions();
    this.drawBackdrop(this.animationProgress);

    if (rawProgress >= 1) {
      this.animating = false;
      Ticker.shared.remove(this.onAnimationTick);

      if (this.animationDirection === 'close') {
        audioService.play('popup_close');
        this.onDismiss?.();
        this.destroy({ children: true });
      }
    }
  };

  private updatePositions(): void {
    if (this.side === 'left') {
      // Panel slides from left: -panelWidth (closed) → 0 (open)
      this.panelBody.x = -this.panelWidth * (1 - this.animationProgress);
    } else {
      // Panel slides from right: screenWidth (closed) → screenWidth - panelWidth (open)
      this.panelBody.x = this.screenWidth - this.panelWidth * this.animationProgress;
    }

    // Close button in top-right of panel
    if (this.closeButton) {
      this.closeButton.x = this.panelWidth - PANEL_DEFAULTS.padding - PANEL_DEFAULTS.closeButtonSize;
      this.closeButton.y = PANEL_DEFAULTS.padding - 4;
    }
  }

  // ===========================================================================
  // DRAWING
  // ===========================================================================

  private drawBackdrop(progress: number): void {
    this.backdrop.clear();
    this.backdrop.rect(0, 0, this.screenWidth, this.screenHeight);
    this.backdrop.fill({ color: 0x000000, alpha: this.backdropAlpha * progress });
  }

  private drawPanel(): void {
    const { panelWidth, screenHeight, backgroundColor, borderColor } = this;

    this.panelBackground.clear();

    // Panel background (full height)
    this.panelBackground.rect(0, 0, panelWidth, screenHeight);
    this.panelBackground.fill(backgroundColor);

    // Border accent on the open edge
    const borderX = this.side === 'left' ? panelWidth - 2 : 0;
    this.panelBackground.rect(borderX, 0, 2, screenHeight);
    this.panelBackground.fill(borderColor);
  }

  private createCloseButton(): void {
    const closeContainer = new Container();
    closeContainer.eventMode = 'static';
    closeContainer.cursor = 'pointer';

    const closeText = new Text({
      text: '×',
      style: {
        fill: PANEL_DEFAULTS.closeButtonColor,
        fontSize: 24,
        fontFamily: 'Roboto, Arial, sans-serif',
        fontWeight: 'bold',
      },
    });
    closeText.anchor.set(0.5, 0.5);
    closeText.x = PANEL_DEFAULTS.closeButtonSize / 2;
    closeText.y = PANEL_DEFAULTS.closeButtonSize / 2;

    const hitArea = new Graphics();
    hitArea.rect(0, 0, PANEL_DEFAULTS.closeButtonSize, PANEL_DEFAULTS.closeButtonSize);
    hitArea.fill({ color: 0x000000, alpha: 0.001 }); // Invisible hit area

    closeContainer.addChild(hitArea);
    closeContainer.addChild(closeText);

    closeContainer.on('pointerover', () => {
      closeText.style.fill = PANEL_DEFAULTS.closeButtonHoverColor;
    });
    closeContainer.on('pointerout', () => {
      closeText.style.fill = PANEL_DEFAULTS.closeButtonColor;
    });
    closeContainer.on('pointerdown', () => { this.close(); });

    this.closeButton = closeContainer;
    this.panelBody.addChild(closeContainer);
  }

  // ===========================================================================
  // CLEANUP
  // ===========================================================================

  override destroy(options?: Parameters<Container['destroy']>[0]): void {
    if (this.animating) {
      Ticker.shared.remove(this.onAnimationTick);
      this.animating = false;
    }
    super.destroy(options);
  }
}
